import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface PartnerProfile {
  id?: string;
  company_name: string;
  company_description: string | null;
  website_url: string | null;
  logo_url: string | null;
  contact_email: string;
  contact_phone: string | null;
  is_verified?: boolean;
}

interface PartnerProfileFormProps {
  initialData?: PartnerProfile;
  onSuccess: () => void;
}

export const PartnerProfileForm = ({ initialData, onSuccess }: PartnerProfileFormProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PartnerProfile>({
    company_name: initialData?.company_name || "",
    company_description: initialData?.company_description || "",
    website_url: initialData?.website_url || "",
    logo_url: initialData?.logo_url || "",
    contact_email: initialData?.contact_email || "",
    contact_phone: initialData?.contact_phone || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (initialData?.id) {
        // Update existing profile
        const { error } = await supabase
          .from("partner_profiles")
          .update({
            company_name: formData.company_name,
            company_description: formData.company_description,
            website_url: formData.website_url,
            logo_url: formData.logo_url,
            contact_email: formData.contact_email,
            contact_phone: formData.contact_phone,
          })
          .eq("id", initialData.id);

        if (error) throw error;
        toast.success("Profile updated successfully!");
      } else {
        // Create new profile
        const { error } = await supabase
          .from("partner_profiles")
          .insert({
            user_id: user.id,
            ...formData,
          });

        if (error) throw error;
        toast.success("Profile created successfully!");
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="company_name">Company Name *</Label>
        <Input
          id="company_name"
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          required
          placeholder="Your Company Inc."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="company_description">Company Description</Label>
        <Textarea
          id="company_description"
          value={formData.company_description || ""}
          onChange={(e) => setFormData({ ...formData, company_description: e.target.value })}
          placeholder="Tell us about your company and what you offer..."
          rows={4}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="website_url">Website URL</Label>
          <Input
            id="website_url"
            type="url"
            value={formData.website_url || ""}
            onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
            placeholder="https://yourcompany.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="logo_url">Logo URL</Label>
          <Input
            id="logo_url"
            type="url"
            value={formData.logo_url || ""}
            onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
            placeholder="https://yourcompany.com/logo.png"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contact_email">Contact Email *</Label>
          <Input
            id="contact_email"
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            required
            placeholder="contact@yourcompany.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="contact_phone">Contact Phone</Label>
          <Input
            id="contact_phone"
            type="tel"
            value={formData.contact_phone || ""}
            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
        </div>
      </div>

      <Button type="submit" disabled={loading} className="w-full md:w-auto">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {initialData ? "Update Profile" : "Create Profile"}
      </Button>
    </form>
  );
};
